import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { getPqrsTypeLabel, PQRS_TYPES } from '../../constants/pqrsTypes.js';
import {
  getPqrsStatusClasses,
  getPqrsStatusLabel,
  PQRS_STATUSES,
} from '../../constants/pqrsStatuses.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  ADMIN_INPUT_CLASS,
} from '../../constants/adminUi.js';

const inputClass = ADMIN_INPUT_CLASS;

const empty = {
  nombre: '',
  email: '',
  telefono: '',
  tipo_pqrs: 'peticion',
  mensaje: '',
  estado: 'pendiente',
  notas_admin: '',
};

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ContactoAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/contacto', {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearch,
          estado: filterEstado || undefined,
          tipo_pqrs: filterTipo || undefined,
        },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, filterEstado, filterTipo]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      nombre: row.nombre ?? '',
      email: row.email ?? '',
      telefono: row.telefono ?? '',
      tipo_pqrs: row.tipo_pqrs ?? 'peticion',
      mensaje: row.mensaje ?? '',
      estado: row.estado ?? 'pendiente',
      notas_admin: row.notas_admin ?? '',
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      nombre: values.nombre.trim(),
      email: values.email.trim(),
      telefono: values.telefono?.trim() ?? '',
      tipo_pqrs: values.tipo_pqrs,
      mensaje: values.mensaje.trim(),
      estado: values.estado,
      notas_admin: values.notas_admin?.trim() ?? '',
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/contacto/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/contacto', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Registrado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar este registro PQRS?', row.nombre);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/contacto/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  const columns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      {
        key: 'fecha_creacion',
        label: 'Fecha',
        render: (row) => formatDateTime(row.fecha_creacion),
      },
      { key: 'nombre', label: 'Nombre' },
      {
        key: 'tipo_pqrs',
        label: 'Tipo',
        render: (row) => getPqrsTypeLabel(row.tipo_pqrs),
      },
      {
        key: 'estado',
        label: 'Estado',
        render: (row) => {
          const meta = PQRS_STATUSES.find((s) => s.value === row.estado) ?? PQRS_STATUSES[0];
          return (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${getPqrsStatusClasses(meta.color)}`}
            >
              {getPqrsStatusLabel(row.estado)}
            </span>
          );
        },
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Ver detalle"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Contacto / PQRS</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Solicitudes enviadas desde el formulario público. Gestiona estado y notas internas de seguimiento.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Registrar PQRS
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <select
          className={`${inputClass} max-w-xs`}
          value={filterEstado}
          onChange={(e) => {
            setFilterEstado(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {PQRS_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className={`${inputClass} max-w-xs`}
          value={filterTipo}
          onChange={(e) => {
            setFilterTipo(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los tipos</option>
          {PQRS_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar PQRS' : 'Nueva PQRS'}
        wide
      >
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Nombre" error={errors.nombre?.message}>
            <input className={inputClass} {...register('nombre', { required: 'Requerido' })} />
          </FormField>
          <FormField label="Correo" error={errors.email?.message}>
            <input type="email" className={inputClass} {...register('email', { required: 'Requerido' })} />
          </FormField>
          <FormField label="Teléfono" error={errors.telefono?.message}>
            <input type="tel" className={inputClass} {...register('telefono')} />
          </FormField>
          <FormField label="Tipo PQRS" error={errors.tipo_pqrs?.message}>
            <select className={inputClass} {...register('tipo_pqrs', { required: 'Requerido' })}>
              {PQRS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Estado" error={errors.estado?.message}>
            <select className={inputClass} {...register('estado', { required: 'Requerido' })}>
              {PQRS_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Mensaje" error={errors.mensaje?.message}>
              <textarea rows={4} className={inputClass} {...register('mensaje', { required: 'Requerido' })} />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Notas internas (administración)">
              <textarea rows={3} className={inputClass} {...register('notas_admin')} />
            </FormField>
          </div>
          <div className="flex gap-3 md:col-span-2">
            <Button type="submit">Guardar</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Detalle PQRS" wide>
        {previewRow ? (
          <div className="space-y-4 text-sm text-slate-300">
            <p>
              <span className="font-semibold text-white">ID:</span> {previewRow.id}
            </p>
            <p>
              <span className="font-semibold text-white">Recibido:</span>{' '}
              {formatDateTime(previewRow.fecha_creacion)}
            </p>
            <p>
              <span className="font-semibold text-white">Nombre:</span> {previewRow.nombre}
            </p>
            <p>
              <span className="font-semibold text-white">Correo:</span> {previewRow.email}
            </p>
            <p>
              <span className="font-semibold text-white">Teléfono:</span> {previewRow.telefono || '—'}
            </p>
            <p>
              <span className="font-semibold text-white">Tipo:</span> {getPqrsTypeLabel(previewRow.tipo_pqrs)}
            </p>
            <p>
              <span className="font-semibold text-white">Estado:</span> {getPqrsStatusLabel(previewRow.estado)}
            </p>
            <div>
              <p className="font-semibold text-white">Mensaje</p>
              <p className="mt-2 whitespace-pre-wrap rounded-lg bg-white/5 p-4 text-slate-300">
                {previewRow.mensaje}
              </p>
            </div>
            {previewRow.notas_admin ? (
              <div>
                <p className="font-semibold text-white">Notas internas</p>
                <p className="mt-2 whitespace-pre-wrap rounded-lg bg-white/5 p-4 text-slate-300">
                  {previewRow.notas_admin}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
